// Search form base class
class SearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (!this.input) return;

    // Listen for form reset
    this.boundOnFormReset = this.onFormReset.bind(this);
    this.input.form?.addEventListener("reset", this.boundOnFormReset);

    // Listen for input changes (debounced)
    this.boundOnChange = webvista.debounce((event) => {
      this.onChange(event);
    }, 300);
    this.input.addEventListener("input", this.boundOnChange);

    // Pop panel for header search
    if (this.hasAttribute("data-pop-panel")) {
      this.boundOnFocusin = this.onFocusin.bind(this);
      this.boundOnFocusout = this.onFocusout.bind(this);
      this.input.addEventListener("focusin", this.boundOnFocusin);
      this.addEventListener("focusout", this.boundOnFocusout);
    }

    // Listen for speech recognition results
    this.boundHandleSpeechResult = this.handleSpeechResult.bind(this);
    this.addEventListener("speech-result", this.boundHandleSpeechResult);
  }

  disconnectedCallback() {
    if (this.boundOnFormReset)
      this.input?.form?.removeEventListener("reset", this.boundOnFormReset);
    if (this.boundOnChange)
      this.input?.removeEventListener("input", this.boundOnChange);
    if (this.boundOnFocusin)
      this.input?.removeEventListener("focusin", this.boundOnFocusin);
    if (this.boundOnFocusout)
      this.removeEventListener("focusout", this.boundOnFocusout);
    if (this.boundHandleSpeechResult)
      this.removeEventListener("speech-result", this.boundHandleSpeechResult);
  }

  /**
   * Toggle reset button visibility based on input value.
   */
  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  /**
   * Handle form reset.
   * @param {Event} event
   */
  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    this.input.value = "";
    this.input.focus();

    // Manually dispatch input event since JS value changes don't trigger it
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
    this.toggleResetButton();
  }

  /**
   * Handle input focus in.
   */
  onFocusin() {
    this.classList.add("in-focus");
    this.isOpen = true;
    this.input.setAttribute("aria-expanded", "true");

    // Activate header to show background color on transparent headers
    this.toggleHeaderActive(true);
  }

  /**
   * Handle search focus out.
   */
  onFocusout() {
    // Defer to get the latest focused element, as focus changes are asynchronous
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      this.classList.remove("in-focus");
      this.isOpen = false;
      this.input.setAttribute("aria-expanded", "false");

      this.toggleHeaderActive(false);
    });
  }

  /**
   * Toggle header active state.
   * @param {boolean} active
   */
  toggleHeaderActive(active = true) {
    this.header = this.header || this.closest(".section-header");
    if (this.header) this.header.classList.toggle("header--is-active", active);
  }

  /**
   * Handle speech recognition result.
   * @param {CustomEvent} event
   */
  handleSpeechResult(event) {
    const { transcript } = event.detail;
    if (!transcript) return;

    this.input.value = transcript;
    this.input.focus();
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
  }
}

window.SearchForm = SearchForm;

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

/**
 * Predictive search class.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};

    // Product type selector
    this.typeSelector = this.querySelector(
      'select[name="filter.p.product_type"]',
    );
    this.searchTerm = "";
    this.isOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.searchResultsWrapper = this.querySelector(".search-results-wrapper");

    // Popular keyword click listeners
    this.boundOnPopularKeywordClick = this.onPopularKeywordClick.bind(this);
    this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
      suggestion.addEventListener("click", this.boundOnPopularKeywordClick);
    });

    this.abortController = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.abortController.abort();

    // Remove popular keyword listeners
    if (this.boundOnPopularKeywordClick) {
      this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
        suggestion.removeEventListener(
          "click",
          this.boundOnPopularKeywordClick,
        );
      });
    }
  }

  /**
   * Build the search query string, including product type filter if selected.
   * @returns {string}
   */
  getQuery() {
    let query = this.input.value.trim();
    const type = this.typeSelector?.value;
    if (type) {
      query += ` AND product_type:${type}`;
    }

    return query;
  }

  /**
   * Handle input value change and trigger search.
   */
  onChange() {
    super.onChange();

    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm === this.searchTerm) return;
    this.searchTerm = currentSearchTerm;

    if (this.input.value.trim().length <= 0) return this.closeResults(true);

    this.showPlaceholder();
    this.getSearchResults();
  }

  /**
   * Handle popular keyword click.
   * @param {Event} event
   */
  onPopularKeywordClick(event) {
    event.preventDefault();

    const keyword = event.currentTarget.getAttribute("data-keyword");
    if (keyword !== "") {
      this.input.value = keyword;
      this.input.focus();

      // Manually dispatch input event since JS value changes don't trigger it
      this.input.dispatchEvent(
        new Event("input", {
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  /**
   * Handle input focus in, re-check search term and display results.
   */
  onFocusin() {
    super.onFocusin();

    // Re-check query in case search term changed from another input
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm && currentSearchTerm === this.searchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.showResults();
    } else {
      this.getSearchResults();
    }
  }

  /**
   * Show loading placeholder while fetching results.
   */
  showPlaceholder() {
    this.placeholder =
      this.placeholder ||
      this.querySelector(`#Placeholder-Search-Form-${this.dataset.section}`)
        ?.innerHTML;
    if (this.placeholder) {
      this.searchResultsWrapper.innerHTML = this.placeholder;
    }
  }

  /**
   * Fetch search results from predictive search API.
   * Uses cached results when available.
   */
  getSearchResults() {
    const queryKey = this.searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoading();

    // Use cached data if available
    if (this.cachedResults[queryKey])
      return this.renderSearchResults(this.cachedResults[queryKey]);

    const url = new URL(
      window.routes.predictive_search_url,
      window.location.origin,
    );

    url.search = new URLSearchParams({
      q: this.searchTerm,
      "resources[limit]": 10,
      "resources[limit_scope]": "each",
      "resources[options][fields]":
        "title,product_type,variants.title,vendor,variants.sku",
      section_id: "predictive-search",
    });

    fetch(url.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(".shopify-section").innerHTML;

        // Sync cached results across all predictive search instances
        document
          .querySelectorAll("predictive-search")
          .forEach((instance) => {
            instance.cachedResults[queryKey] = resultsMarkup;
          });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;

        this.removeAttribute("loading");
        this.searchResultsWrapper.innerHTML = "";
        this.removeAttribute("results");
      });
  }

  /**
   * Render search results HTML into the results container.
   * @param {string} resultsHtml
   */
  renderSearchResults(resultsHtml) {
    this.searchResultsWrapper.innerHTML = resultsHtml;
    this.setAttribute("results", "true");

    this.setLiveRegionResults();
    this.showResults();

    webvista.initLazyImages();
    webvista.initToolTips();
  }

  /**
   * Calculate and return the maximum height for the results container.
   * @returns {number}
   */
  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      this.querySelector(".predictive-search-main").getBoundingClientRect()
        .top -
      32;

    return this.resultsMaxHeight;
  }

  /**
   * Show predictive search results panel.
   * The 'open' attribute controls visibility.
   */
  showResults() {
    this.style.setProperty(
      "--result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`,
    );
    this.setAttribute("open", "true");
  }

  /**
   * Close predictive search results.
   * The 'open' attribute controls the results container visibility,
   * the 'results' attribute tracks whether data exists.
   * @param {boolean} clearSearchTerm - Whether to clear search data
   */
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.searchResultsWrapper.innerHTML = "";
      this.removeAttribute("results");
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");

    this.resultsMaxHeight = false;
    this.style.removeProperty("--result-max-height");
  }

  /**
   * Set live region to loading state for assistive devices.
   */
  setLiveRegionLoading() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");
    this.setAttribute("loading", "true");
    this.setLiveRegionText(this.loadingText);
  }

  /**
   * Update live region with search results count for assistive devices.
   */
  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  /**
   * Set live region text for assistive devices.
   * @param {string} statusText
   */
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
